import { MetadataField } from '@/api/generatedApi';
import { useUpdateMetadataField } from '@/hooks/api/metadata/useUpdateMetadataField';
import { useToast } from '@/hooks/useToast';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useState } from 'react';
import { MetadataTestIds } from '@/enums/metadataTestIds.enum';

interface EditMetadataFieldModalProperties {
  isOpen: boolean;
  onClose: () => void;
  metadataField: MetadataField;
  onSuccess?: (newEntry: MetadataField) => void;
}

export const EditMetadataFieldModal = ({
  isOpen,
  onClose,
  metadataField,
  onSuccess,
}: EditMetadataFieldModalProperties) => {
  const { showToast } = useToast();
  const [name, setName] = useState(metadataField.name);
  const [error, setError] = useState<string | undefined>();
  const { updateMetadataField, isUpdating } = useUpdateMetadataField({
    onSuccess: () => {
      showToast({
        message: 'Metadata field updated successfully',
        type: 'success',
      });
      onSuccess?.({ ...metadataField, name });
      onClose();
    },
    onError: (errorMessage: string) => {
      setError(errorMessage);
      showToast({
        message: 'Failed to update metadata field',
        type: 'error',
      });
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(undefined);
    updateMetadataField({ fieldId: metadataField.id, data: { name } });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      onPointerDown={event => event.stopPropagation()}
      onKeyDown={event => event.stopPropagation()}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Edit Metadata Field</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label='Name'
              value={name}
              onChange={event => setName(event.target.value)}
              required
              error={!!error}
              helperText={error}
              slotProps={{
                input: {
                  inputProps: { 'data-testid': MetadataTestIds.MetadataFieldNameInput },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type='submit'
            variant='contained'
            disabled={isUpdating}
            data-testid={MetadataTestIds.EditMetadataFieldSubmitButton}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
